import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import TagsCell from "@via/components/tags-cell.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table from "@via/components/table/table.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import ProjectsFilter from "@via/pages/projects/projects-filter.tsx";
import { useProjectList } from "@via/pages/projects/projects.api.tsx";
import {
  EMPTY_TABLE_MESSAGE,
  ERROR,
  PROJECTS_TABLE,
  ROUTES,
  USER_ROLES,
} from "@via/utils/constants.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import { TableContextMenuInterface } from "@via/utils/interfaces";
import { type ListProjectsResponseBodyItem } from "common";
import { useCallback } from "react";
import { Link } from "react-router-dom";

const columnHelper = createColumnHelper<ListProjectsResponseBodyItem>();

type FilterItem = { id: string; value: unknown };

interface ProjectsTableProps {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  sort: Array<{ id: string; desc: boolean }>;
  setSort: (sort: Array<{ id: string; desc: boolean }>) => void;
  columnFilters: FilterItem[];
  setColumnFilters: (
    filters: (prevFilters: FilterItem[]) => FilterItem[]
  ) => void;
  handleAction?: (
    option: TableContextMenuInterface,
    project: ListProjectsResponseBodyItem
  ) => void;
}

const ProjectsTable = ({
  page,
  setPage,
  pageSize,
  setPageSize,
  sort,
  setSort,
  columnFilters,
  setColumnFilters,
  handleAction,
}: ProjectsTableProps) => {
  const { user } = useAuth();
  const isAdmin = user.userRole === USER_ROLES.ADMIN;

  const { isLoading, isError, data } = useProjectList(
    page,
    pageSize,
    sort,
    columnFilters
  );

  const getProjectsContext = useCallback(() => {
    const projects = data?.data || [];

    return {
      projects: projects.map(project => ({
        id: project.id,
        name: project.name,
        owner: project.owner,
        createdAt: project.createdAt,
        updatedAt: project.updatedAt,
        own: project.own,
      })),
      total: data?.total || 0,
      currentPage: page,
      pageSize: pageSize,
      filters: columnFilters,
      sortBy: sort.length > 0 ? sort[0] : null,
    };
  }, [data?.data, data?.total, page, pageSize, sort, columnFilters]);

  useRegisterCortexContext({
    routePattern: ROUTES.PROJECTS,
    contextName: "Project List",
    getContext: getProjectsContext,
    systemPrompt:
      "You are helping users manage projects. Answer questions about projects, help find specific projects, and assist with filtering and sorting.",
  });

  const columns = [
    columnHelper.accessor("name", {
      cell: info => {
        return (
          <Link to={info.row.original.id.toString()} className="via-link">
            {info.getValue()}
          </Link>
        );
      },
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor("owner", {
      cell: info => info.getValue(),
      header: () => <span>Owner</span>,
    }),
    columnHelper.accessor("createdAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LLL")}</span>;
      },
      header: () => <span>Date Created</span>,
    }),
    columnHelper.accessor("updatedAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LLL")}</span>;
      },
      header: () => <span>Last Updated</span>,
    }),
    columnHelper.display({
      id: "tags",
      cell: ({ row }) => (
        <TagsCell tags={row.original.tags ?? []} rowId={row.original.id} />
      ),
      header: () => <span>Tags</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("id", {
      cell: info => {
        const own = info.row.original.own;
        return own || isAdmin ? (
          <TableContextMenu
            options={PROJECTS_TABLE}
            onSelect={option => {
              handleAction?.(option, info.row.original);
            }}
          />
        ) : null;
      },
      header: () => "Options",
      enableSorting: false,
    }),
  ] as Array<ColumnDef<ListProjectsResponseBodyItem>>;

  return isLoading ? (
    <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
      <SkeletonLoader count={12} />
    </div>
  ) : isError ? (
    <div className="via-generic-error">
      <GenericError message={ERROR} />
    </div>
  ) : (
    <Table
      data={data?.data}
      columns={columns}
      sort={sort}
      setSort={setSort}
      page={page}
      setPage={setPage}
      pageSize={pageSize}
      setPageSize={setPageSize}
      total={data?.total}
      emptyMessage={EMPTY_TABLE_MESSAGE.PROJECTS}
      additionalFilters={
        <ProjectsFilter
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      }
    />
  );
};

export default ProjectsTable;
